import React, { useState, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight, Target, Edit3, BarChart3, CheckCircle, XCircle, Layers, TreePine, Download, Clock, Zap } from 'lucide-react';
import deepseekApi from '../services/deepseekApi';
import englishDataService from '../services/englishDataService';
import { rewriteExercisesData, generateExercisesFromSentences, validateRewriteAnswer } from '../data/rewriteExercisesData';
import LoadingSpinner from './LoadingSpinner';
import { 
  ESSToolboxContent, 
  AnalysisTab, 
  VisualizationTab, 
  QuizTab, 
  RewriteTab 
} from './ESSToolboxTabs';

const ESSToolboxEnhanced = () => {
  const [activeTab, setActiveTab] = useState('analyze');
  const [inputText, setInputText] = useState('');
  const [analyzedSentences, setAnalyzedSentences] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [rewriteExercises, setRewriteExercises] = useState([]);
  const [passageStructures, setPassageStructures] = useState([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentRewriteIndex, setCurrentRewriteIndex] = useState(0);
  const [rewriteAnswers, setRewriteAnswers] = useState({});
  const [selectedRewriteType, setSelectedRewriteType] = useState('');
  const [showExample, setShowExample] = useState(false);
  const [checkResult, setCheckResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('analyzing');
  const [deepseekApiResponse, setDeepseekApiResponse] = useState(null);
  const [importedData, setImportedData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dsCategory, setDsCategory] = useState('Action');
  const [dsDifficulty, setDsDifficulty] = useState('');
  
  // Supabase data states
  const [essData, setEssData] = useState(null);
  const [samplePassages, setSamplePassages] = useState([]);
  const [sentencePatterns, setSentencePatterns] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  // DeepSeek stories from Supabase (eng_esstoolbox)
  const [dsStories, setDsStories] = useState([]);
  const [selectedStoryId, setSelectedStoryId] = useState('');
  
  // Analysis stats for tracking time, tokens, and cost
  const [analysisStats, setAnalysisStats] = useState({
    startTime: null,
    endTime: null,
    timeElapsed: 0,
    tokensUsed: 0,
    inputTokens: 0,
    outputTokens: 0,
    sentencesAnalyzed: 0,
    isAnalyzing: false,
    cost: 0,
    isStandardTime: true
  });

  // 句型改寫選項 - 將根據 DeepSeek API 響應動態生成
  const [rewriteOptions, setRewriteOptions] = useState([
    { value: '', label: '-- 請選擇 --' }
  ]);

  // Load data from A2A backend on component mount
  useEffect(() => {
    const loadESSData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);
        
        const [essDataResult, samplePassagesResult, sentencePatternsResult, stories] = await Promise.all([
          englishDataService.getESSAllData(),
          englishDataService.getESSSamplePassages({ limit: 100 }),
          englishDataService.getESSSentencePatterns(),
          englishDataService.getESSDeepSeekStories()
        ]);
        
        // Handle response formats
        setEssData(essDataResult);
        setSamplePassages(Array.isArray(samplePassagesResult) ? samplePassagesResult : (samplePassagesResult.data || samplePassagesResult.passages || []));
        setSentencePatterns(sentencePatternsResult);
        setDsStories(Array.isArray(stories) ? stories : (stories.data || stories.stories || []));
      } catch (error) {
        console.error('Error loading ESS data:', error);
        setDataError(error.message);
      } finally {
        setDataLoading(false);
      }
    };

    loadESSData();
  }, []);

  // When selecting a DeepSeek story, set input and clear previous results
  const handleSelectStory = (storyId) => {
    setSelectedStoryId(storyId);
    const story = (dsStories || []).find(s => s.id === storyId);
    if (story && story.content) {
      setInputText(story.content);
      if (story.category) setDsCategory(story.category);
      if (story.difficulty) setDsDifficulty(story.difficulty);
      // Prefill analysis if raw JSON exists
      const ds = story.raw || {};
      if (Array.isArray(ds.visualization)) setPassageStructures(ds.visualization);
      if (Array.isArray(ds.quiz?.questions)) setQuizQuestions(ds.quiz.questions);
      if (Array.isArray(ds.rewrite?.exercises)) setRewriteExercises(ds.rewrite.exercises);
      setAnalyzedSentences([]); // force fresh analyze if needed
    }
  };

  // Save current imported JSON (or current analysis) to Supabase
  const handleSaveToSupabase = async () => {
    try {
      if (!importedData && !deepseekApiResponse) {
        alert('No JSON to save. Please Import JSON or run an analysis first.');
        return;
      }

      setSaving(true);
      const raw = importedData || {
        schema: 'ess_toolbox_analysis_v1',
        generatedAt: new Date().toISOString(),
        passage: { title: null, text: inputText },
        sentences: analyzedSentences.map(s => s.original),
        analyses: deepseekApiResponse?.apiResults || analyzedSentences.map(s => s.detailedAnalysis).filter(Boolean),
        visualization: passageStructures,
        quiz: { questions: quizQuestions },
        rewrite: { exercises: rewriteExercises },
        stats: analysisStats
      };

      // Prefer explicit title from imported JSON; otherwise fall back to filename-derived label or a short snippet
      const firstLine = (inputText || '').split('\n')[0]?.trim() || '';
      const snippet = firstLine.replace(/\s+/g, ' ').slice(0, 60) + (firstLine.length > 60 ? '…' : '');
      const title = (raw.passage && raw.passage.title)
        || (importedData && importedData._fileNameStem)
        || (snippet || 'ESS Sample');
      const category = dsCategory || 'ESS';
      const content = raw.passage?.text || inputText || '';

      // Generate stable story id slug
      const slugBase = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'ess-sample';
      const storyId = `${slugBase}-${Date.now()}`;

      await englishDataService.saveESSStory({
        storyId,
        title,
        category,
        difficulty: dsDifficulty || null,
        content,
        raw
      });

      // Refresh dropdown
      const stories = await englishDataService.getESSDeepSeekStories();
      setDsStories(Array.isArray(stories) ? stories : (stories.data || stories.stories || []));
      setSelectedStoryId(storyId);
      alert('Saved to A2A backend successfully.');
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save to A2A backend.');
    } finally {
      setSaving(false);
    }
  };

  // DeepSeek API pricing calculation function
  const calculateCost = (inputTokens, outputTokens, isStandardTime = true) => {
    // Convert tokens to millions
    const inputTokensMillion = inputTokens / 1000000;
    const outputTokensMillion = outputTokens / 1000000;
    
    let inputCost, outputCost;
    
    if (isStandardTime) {
      // Standard time pricing (08:30-00:30 Beijing time)
      inputCost = inputTokensMillion * 2; // Cache miss rate
      outputCost = outputTokensMillion * 8;
    } else {
      // Discount time pricing (00:30-08:30 Beijing time)
      inputCost = inputTokensMillion * 1; // 5折 discount
      outputCost = outputTokensMillion * 4;
    }
    
    return {
      inputCost: inputCost,
      outputCost: outputCost,
      totalCost: inputCost + outputCost
    };
  };

  // Check if current time is in standard pricing period (Beijing time)
  const checkStandardTime = () => {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000)); // UTC+8
    const hour = beijingTime.getUTCHours();
    
    // Standard time: 08:30-00:30 (8:30 AM to 12:30 AM next day)
    // Discount time: 00:30-08:30 (12:30 AM to 8:30 AM)
    return hour >= 8.5 || hour < 0.5;
  };

  // 切換展開狀態
  const toggleExpand = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // 分析段落 - 使用 DeepSeek API
  const analyzePassage = async () => {
    if (!inputText.trim()) {
      alert('請輸入要分析的段落');
      return;
    }

    const startTime = Date.now();
    const isStandardTime = checkStandardTime();
    
    setAnalysisStats(prev => ({
      ...prev,
      startTime: startTime,
      isAnalyzing: true,
      timeElapsed: 0,
      tokensUsed: 0,
      inputTokens: 0,
      outputTokens: 0,
      sentencesAnalyzed: 0,
      cost: 0,
      isStandardTime: isStandardTime
    }));

    setIsLoading(true);
    setProgress(0);
    setCurrentStep('analyzing');
    
    try {
      const sentences = inputText.split(/[.!?]+/).filter(s => s.trim().length > 0);
      
      // 使用 DeepSeek API 分析每個句子
      const analyzedResults = [];
      const visualStructures = [];
      
      for (let i = 0; i < sentences.length; i++) {
        const sentence = sentences[i].trim();
        if (sentence.length > 0) {
          // Update progress for each sentence analysis
          setProgress(Math.round((i / sentences.length) * 40));
          
          const analysis = await deepseekApi.analyzeSentenceStructure(sentence);
          
          analyzedResults.push({
            id: i,
            original: sentence,
            type: analysis.mainPattern,
            category: 'AI分析',
            subCategory: analysis.basicStructure,
            legend: analysis.legendStructure || analysis.mainPattern,
            words: sentence.split(/\s+/),
            detailedAnalysis: analysis
          });

          visualStructures.push({
            id: i,
            sentence: sentence,
            analysis: {
              mainType: analysis.mainPattern,
              basicStructure: analysis.basicStructure,
              legendStructure: analysis.legendStructure,
              details: analysis.detailedAnalysis && typeof analysis.detailedAnalysis === 'object' 
                ? Object.keys(analysis.detailedAnalysis).join(', ') 
                : '',
              subclauses: Array.isArray(analysis.specialFeatures) 
                ? analysis.specialFeatures.map(item => typeof item === 'string' ? item : JSON.stringify(item))
                : []
            },
            conditionClause: analysis.subordinateClauses && analysis.subordinateClauses.length > 0 ? {
              content: typeof analysis.subordinateClauses[0] === 'string' ? analysis.subordinateClauses[0] : JSON.stringify(analysis.subordinateClauses[0]),
              type: 'Subordinate Clause',
              description: '從句'
            } : null
          });
        }
      }

      // Save API response in strict structure for JSON export
      setDeepseekApiResponse({
        timestamp: new Date().toISOString(),
        inputText,
        sentences,
        apiResults: analyzedResults.map(r => r.detailedAnalysis),
        analysisStats: { startTime, endTime: Date.now(), sentencesAnalyzed: sentences.length }
      });

      setAnalyzedSentences(analyzedResults);
      setPassageStructures(visualStructures);
      setProgress(50);
      setCurrentStep('generating_quiz');

      // 生成測驗題目
      await generateEnhancedQuizQuestions(sentences);
      setProgress(75);
      setCurrentStep('creating_exercises');

      // 生成改寫練習
      await generateEnhancedRewriteExercises(sentences);
      setProgress(100);

    } catch (error) {
      console.error('Analysis error:', error);
      alert('分析過程中發生錯誤，請稍後再試');
    } finally {
      // Calculate final statistics with cost
      const endTime = Date.now();
      const timeElapsed = Math.floor((endTime - startTime) / 1000);
      
      // Get sentences for token calculation
      const sentences = inputText.split(/[.!?]+/).filter(s => s.trim().length > 0);
      
      // Estimate tokens (more accurate estimation)
      const inputTokens = Math.floor(inputText.length / 4) + (sentences.length * 100); // Input tokens
      const outputTokens = Math.floor(inputText.length / 3) + (sentences.length * 150); // Output tokens (typically larger)
      const totalTokens = inputTokens + outputTokens;
      
      // Calculate cost based on DeepSeek API pricing
      const costCalculation = calculateCost(inputTokens, outputTokens, isStandardTime);
      
      setAnalysisStats(prev => ({
        ...prev,
        endTime: endTime,
        timeElapsed: timeElapsed,
        tokensUsed: totalTokens,
        inputTokens: inputTokens,
        outputTokens: outputTokens,
        sentencesAnalyzed: sentences.length,
        cost: costCalculation.totalCost,
        isAnalyzing: false,
        isStandardTime: isStandardTime
      }));

      setIsLoading(false);
      setProgress(0);
      setCurrentStep('analyzing');
    }
  };

  // JSON Download (strict schema)
  const downloadJSON = () => {
    if (!deepseekApiResponse && analyzedSentences.length === 0) {
      alert('No analysis data available. Please run analysis first.');
      return;
    }

    const sentences = analyzedSentences.length > 0
      ? analyzedSentences.map(s => s.original)
      : (deepseekApiResponse?.sentences || []);

    const analyses = (deepseekApiResponse?.apiResults && deepseekApiResponse.apiResults.length)
      ? deepseekApiResponse.apiResults
      : analyzedSentences.map(s => s.detailedAnalysis).filter(Boolean);

    const payload = {
      schema: 'ess_toolbox_analysis_v1',
      generatedAt: new Date().toISOString(),
      passage: { title: null, text: (inputText || '').trim() },
      sentences,
      analyses,
      visualization: passageStructures,
      quiz: { questions: quizQuestions },
      rewrite: { exercises: rewriteExercises },
      stats: analysisStats
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ESS_Toolbox_Analysis_${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // JSON Import (strict schema with backward compatibility)
  const importJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);

        const passageText = data.passage?.text || data.inputText || '';
        const sentences = Array.isArray(data.sentences) && data.sentences.length
          ? data.sentences
          : (Array.isArray(data.analyzedSentences) ? data.analyzedSentences.map(s => s.original) : []);

        const analyses = Array.isArray(data.analyses) && data.analyses.length
          ? data.analyses
          : (data.deepseekApiResponse?.apiResults || (Array.isArray(data.analyzedSentences) ? data.analyzedSentences.map(s => s.apiData || s.detailedAnalysis).filter(Boolean) : []));

        if (!passageText || sentences.length === 0 || analyses.length === 0) {
          alert('Invalid data format. The file must include passage text, sentences, and analyses.');
          return;
        }

        setInputText(passageText);

        const rebuiltAnalyzed = sentences.map((sentence, index) => {
          const ai = analyses[index] || {};
          return {
            id: index,
            original: sentence,
            type: ai.mainPattern || 'S+V',
            category: 'AI分析',
            subCategory: ai.basicStructure || '基礎句型',
            legend: ai.legendStructure || '(S)+(V)',
            words: sentence.trim().split(/\s+/),
            detailedAnalysis: ai
          };
        });
        setAnalyzedSentences(rebuiltAnalyzed);

        const rebuiltVisualization = sentences.map((sentence, index) => {
          const ai = analyses[index] || {};
          const details = ai.detailedAnalysis && typeof ai.detailedAnalysis === 'object'
            ? Object.keys(ai.detailedAnalysis).join(', ')
            : '';
          const subclauses = Array.isArray(ai.specialFeatures)
            ? ai.specialFeatures.map(item => typeof item === 'string' ? item : JSON.stringify(item))
            : [];
          const hasSubs = Array.isArray(ai.subordinateClauses) && ai.subordinateClauses.length > 0;
          const firstSub = hasSubs ? ai.subordinateClauses[0] : null;
          return {
            id: index,
            sentence,
            analysis: {
              mainType: ai.mainPattern || 'S+V',
              basicStructure: ai.basicStructure || '',
              legendStructure: ai.legendStructure || '',
              details: details || '基礎句型分析',
              subclauses
            },
            conditionClause: hasSubs ? {
              content: typeof firstSub === 'string' ? firstSub : JSON.stringify(firstSub),
              type: 'Subordinate Clause',
              description: '從句'
            } : null
          };
        });
        setPassageStructures(rebuiltVisualization);

        if (data.quiz?.questions) {
          setQuizQuestions(data.quiz.questions);
          setCurrentQuizIndex(0);
          setQuizAnswers({});
        }
        if (data.rewrite?.exercises) {
          setRewriteExercises(data.rewrite.exercises);
          setCurrentRewriteIndex(0);
          setRewriteAnswers({});
        }

        setAnalysisStats(data.stats || data.analysisStats || analysisStats);
        setDeepseekApiResponse({
          timestamp: data.generatedAt || data.timestamp || new Date().toISOString(),
          inputText: passageText,
          sentences,
          apiResults: analyses,
          analysisStats: data.stats || data.analysisStats || {}
        });
        setImportedData(data);

        alert('Data imported successfully!');
      } catch (err) {
        console.error('Import JSON parse error:', err);
        alert('Error parsing the JSON file. Please verify the file format.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // 生成增強版測驗題目（使用 DeepSeek API）
  const generateEnhancedQuizQuestions = async (sentences) => {
    try {
      const apiResult = await deepseekApi.generateQuizQuestions(sentences);
      const questions = apiResult.questions || (essData?.mockQuizQuestions || []);
      setQuizQuestions(questions);
      setCurrentQuizIndex(0);
      setQuizAnswers({});
      setShowQuizResults(false);
    } catch (error) {
      console.error('Quiz generation error:', error);
      setQuizQuestions(essData?.mockQuizQuestions || []);
    }
  };

  // 生成增強版改寫練習（使用 DeepSeek API）
  const generateEnhancedRewriteExercises = async (sentences) => {
    try {
      // 為每個句子生成改寫練習
      const exercises = [];
      const allRewriteOptions = new Set();
      
      for (let i = 0; i < Math.min(sentences.length, 5); i++) {
        const sentence = sentences[i].trim();
        if (sentence.length > 0) {
          // 使用 DeepSeek API 分析句子結構
          const analysis = await deepseekApi.analyzeSentenceStructure(sentence);
          
          // 從 API 響應中提取 rewriteOptions
          const rewriteOptionsFromAPI = analysis.rewriteOptions || {};
          
          // 轉換為練習格式
          const exercise = {
            id: i + 1,
            sentence: sentence,
            originalAnalysis: analysis,
            examples: rewriteOptionsFromAPI,
            instructions: {
              'S+V': '請將句子改寫為最簡單的主詞+動詞結構，描述基本動作或狀態。',
              'S+V+O': '請將句子改寫為主詞+動詞+受詞的結構，突出動作的對象。',
              'S+V+C': '請將句子改寫為主詞+動詞+補語的結構，描述狀態或屬性。',
              'S+V+IO+DO': '請將句子改寫為雙受詞結構，明確指出受益者和動作對象。',
              'S+V+O+C': '請將句子改寫為受詞補語結構，描述受詞的狀態或特徵。'
            }
          };
          
          exercises.push(exercise);
          
          // 收集所有可用的句型選項
          Object.keys(rewriteOptionsFromAPI).forEach(pattern => {
            allRewriteOptions.add(pattern);
          });
        }
      }
      
      // 更新句型選項
      const options = [
        { value: '', label: '-- 請選擇 --' },
        ...Array.from(allRewriteOptions).map(pattern => ({
          value: pattern,
          label: pattern
        }))
      ];
      setRewriteOptions(options);
      
      setRewriteExercises(exercises);
      setCurrentRewriteIndex(0);
      setRewriteAnswers({});
      setSelectedRewriteType('');
      setShowExample(false);
      setCheckResult('');
    } catch (error) {
      console.error('Rewrite exercises generation error:', error);
      setRewriteExercises(essData?.mockRewriteExercises || []);
      // 使用默認選項
      setRewriteOptions([
        { value: '', label: '-- 請選擇 --' },
        { value: 'S+V', label: 'S+V (主詞 + 動詞)' },
        { value: 'S+V+O', label: 'S+V+O (主詞 + 動詞 + 受詞)' },
        { value: 'S+V+C', label: 'S+V+C (主詞 + 動詞 + 補語)' },
        { value: 'S+V+IO+DO', label: 'S+V+IO+DO (主詞 + 動詞 + 間接受詞 + 直接受詞)' },
        { value: 'S+V+O+C', label: 'S+V+O+C (主詞 + 動詞 + 受詞 + 補語)' }
      ]);
      
      // 使用默認練習數據
      const defaultExercises = generateExercisesFromSentences(rewriteExercisesData.sampleSentences);
      setRewriteExercises(defaultExercises);
    }
  };

  // 測驗相關函數
  const handleQuizAnswer = (questionId, optionIndex) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const nextQuizQuestion = () => {
    if (currentQuizIndex < quizQuestions.length - 1) {
      setCurrentQuizIndex(currentQuizIndex + 1);
    }
  };

  const prevQuizQuestion = () => {
    if (currentQuizIndex > 0) {
      setCurrentQuizIndex(currentQuizIndex - 1);
    }
  };

  // 改寫練習相關函數
  const handleRewriteAnswer = (exerciseId, answer) => {
    setRewriteAnswers(prev => ({
      ...prev,
      [exerciseId]: answer
    }));
    setCheckResult('');
    setShowExample(false);
  };

  const handleRewriteTypeChange = (type) => {
    setSelectedRewriteType(type);
    setShowExample(false);
    setCheckResult('');
  };

  const nextRewriteExercise = () => {
    if (currentRewriteIndex < rewriteExercises.length - 1) {
      setCurrentRewriteIndex(currentRewriteIndex + 1);
      setSelectedRewriteType('');
      setShowExample(false);
      setCheckResult('');
    }
  };

  const prevRewriteExercise = () => {
    if (currentRewriteIndex > 0) {
      setCurrentRewriteIndex(currentRewriteIndex - 1);
      setSelectedRewriteType('');
      setShowExample(false);
      setCheckResult('');
    }
  };

  // 檢查按鈕功能
  const checkRewriteAnswer = () => {
    const exercise = rewriteExercises[currentRewriteIndex];
    const userAnswer = rewriteAnswers[exercise.id] || '';
    
    if (!selectedRewriteType) {
      setCheckResult('請先選擇要改寫的句型類型。');
      return;
    }
    
    if (!userAnswer.trim()) {
      setCheckResult('請先輸入您的改寫句子。');
      return;
    }

    // 使用新的驗證系統
    const validationResult = validateRewriteAnswer(userAnswer, selectedRewriteType, rewriteExercisesData.validationRules);
    const isCorrect = validationResult.isCorrect;
    const feedback = validationResult.feedback;

    setCheckResult(feedback);
  };

  // 顯示範例按鈕功能
  const showRewriteExample = () => {
    if (!selectedRewriteType) {
      setCheckResult('請先選擇要改寫的句型類型。');
      return;
    }
    
    const exercise = rewriteExercises[currentRewriteIndex];
    const example = exercise.examples[selectedRewriteType];
    
    if (example) {
      setShowExample(true);
      setCheckResult('');
    } else {
      setCheckResult('此句型類型暫無範例。');
    }
  };

  // 清空按鈕功能
  const clearRewriteAnswer = () => {
    const exercise = rewriteExercises[currentRewriteIndex];
    setRewriteAnswers(prev => ({
      ...prev,
      [exercise.id]: ''
    }));
    setCheckResult('');
    setShowExample(false);
  };

  // Download HTML function
  const downloadHTML = () => {
    if (passageStructures.length === 0) {
      alert('請先完成段落分析，然後再下載報告');
      return;
    }

    const generateHTML = () => {
      const timestamp = new Date().toLocaleString('zh-TW');
      const stats = analysisStats;
      const pricingPeriod = stats.isStandardTime ? '標準時段' : '優惠時段';
      const pricingInfo = stats.isStandardTime 
        ? '標準時段價格 (北京時間 08:30-00:30)'
        : '優惠時段價格 (北京時間 00:30-08:30) - 5折優惠';
      
      let html = `
<!DOCTYPE html>
<html lang="zh-TW">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>EnglishDaily - Passage 句型結構樹狀視覺化報告</title>
    <style>
        body {
            font-family: 'Microsoft JhengHei', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f8f9fa;
        }
        
        /* Header with Download Button */
        .header-section {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding: 20px;
            background: white;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header-title {
            font-size: 24px;
            font-weight: bold;
            color: #333;
        }
        .download-btn {
            background: #10b981;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
        }
        .download-btn:hover {
            background: #059669;
            transform: translateY(-1px);
        }
        
        /* Analysis Statistics Section */
        .stats-section {
            background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
            border: 1px solid #3b82f6;
            border-radius: 10px;
            padding: 20px;
            margin-bottom: 20px;
        }
        .stats-title {
            font-size: 18px;
            font-weight: 600;
            color: #1e40af;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
        }
        .stat-card {
            background: white;
            padding: 15px;
            border-radius: 8px;
            border: 1px solid #d1d5db;
            text-align: center;
        }
        .stat-value {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
        }
        .stat-label {
            font-size: 14px;
            color: #6b7280;
        }
        .stat-card.blue .stat-value { color: #3b82f6; }
        .stat-card.purple .stat-value { color: #8b5cf6; }
        .stat-card.green .stat-value { color: #10b981; }
        .stat-card.orange .stat-value { color: #f59e0b; }
        
        /* Cost Analysis Section */
        .cost-section {
            background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
            border: 1px solid #ef4444;
            border-radius: 10px;
            padding: 20px;
            margin-bottom: 20px;
        }
        .cost-title {
            font-size: 18px;
            font-weight: 600;
            color: #dc2626;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .cost-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
        }
        .cost-card {
            background: white;
            padding: 15px;
            border-radius: 8px;
            border: 1px solid #fca5a5;
            text-align: center;
        }
        .cost-value {
            font-size: 20px;
            font-weight: bold;
            color: #dc2626;
            margin-bottom: 5px;
        }
        .cost-label {
            font-size: 12px;
            color: #6b7280;
        }
        .pricing-note {
            margin-top: 15px;
            font-size: 12px;
            color: #6b7280;
            text-align: center;
        }
        
        /* Sentence Analysis Section */
        .sentence-section {
            background: white;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            margin-bottom: 20px;
            overflow: hidden;
        }
        .sentence-header {
            background: #f3f4f6;
            padding: 15px 20px;
            border-bottom: 1px solid #e5e7eb;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: space-between;
            transition: background-color 0.2s;
        }
        .sentence-header:hover {
            background: #e5e7eb;
        }
        .sentence-text {
            font-size: 16px;
            font-weight: 500;
            color: #374151;
            flex: 1;
        }
        .chevron {
            font-size: 18px;
            color: #6b7280;
            transition: transform 0.2s;
        }
        .chevron.expanded {
            transform: rotate(90deg);
        }
        .sentence-content {
            padding: 20px;
            display: none;
        }
        .sentence-content.expanded {
            display: block;
        }
        
        /* Analysis Layout - Single Column with Sections */
        .analysis-container {
            background: white;
            border: 1px solid #dbeafe;
            border-radius: 8px;
            padding: 20px;
            margin-top: 15px;
        }
        .analysis-section {
            margin-bottom: 20px;
        }
        .analysis-section:last-child {
            margin-bottom: 0;
        }
        .section-label {
            font-weight: 600;
            color: #374151;
            font-size: 14px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
        }
        .section-content {
            color: #6b7280;
            font-size: 14px;
            line-height: 1.4;
        }
        .pattern-highlight {
            color: #f59e0b;
            font-weight: 600;
        }
        .legend-structure {
            margin-top: 10px;
        }
        .legend-title {
            font-weight: 600;
            color: #374151;
            font-size: 12px;
            margin-bottom: 5px;
        }
        .legend-content {
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
        }
        .legend-item {
            background: #dbeafe;
            color: #1e40af;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-family: monospace;
        }
        
        .subordinate-json {
            background: #f3f4f6;
            padding: 10px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 12px;
            color: #374151;
            line-height: 1.4;
        }
        
        /* Footer */
        .footer {
            text-align: center;
            margin-top: 40px;
            padding: 20px;
            background: white;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            color: #6b7280;
            font-size: 14px;
        }
        
        /* Icons */
        .icon {
            width: 20px;
            height: 20px;
            display: inline-block;
        }
    </style>
</head>
<body>
    <!-- Header with Download Button -->
    <div class="header-section">
        <div class="header-title">Passage 句型結構樹狀視覺化</div>
        <button class="download-btn">
            <svg class="icon" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
            下載 HTML 報告
        </button>
    </div>

    <!-- Analysis Statistics -->
    <div class="stats-section">
        <div class="stats-title">
            <svg class="icon" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"></path>
            </svg>
            分析統計
        </div>
        <div class="stats-grid">
            <div class="stat-card blue">
                <div class="stat-value">${stats.timeElapsed}s</div>
                <div class="stat-label">分析時間</div>
            </div>
            <div class="stat-card purple">
                <div class="stat-value">${stats.tokensUsed}</div>
                <div class="stat-label">總 Token</div>
            </div>
            <div class="stat-card green">
                <div class="stat-value">${stats.sentencesAnalyzed}</div>
                <div class="stat-label">分析句子</div>
            </div>
            <div class="stat-card orange">
                <div class="stat-value">${passageStructures.length}</div>
                <div class="stat-label">視覺化結構</div>
            </div>
        </div>
    </div>

    <!-- Cost Analysis -->
    <div class="cost-section">
        <div class="cost-title">
            <svg class="icon" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"></path>
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clip-rule="evenodd"></path>
            </svg>
            成本分析
        </div>
        <div class="cost-grid">
            <div class="cost-card">
                <div class="cost-value">${stats.inputTokens}</div>
                <div class="cost-label">輸入 Token</div>
            </div>
            <div class="cost-card">
                <div class="cost-value">${stats.outputTokens}</div>
                <div class="cost-label">輸出 Token</div>
            </div>
            <div class="cost-card">
                <div class="cost-value">¥${stats.cost.toFixed(4)}</div>
                <div class="cost-label">總成本 (¥)</div>
            </div>
            <div class="cost-card">
                <div class="cost-value">${pricingPeriod}</div>
                <div class="cost-label">計費時段</div>
            </div>
        </div>
        <div class="pricing-note">
            ${stats.isStandardTime ? 
                '標準時段 (08:30-00:30): 輸入 2元/百萬tokens, 輸出 8元/百萬tokens' : 
                '優惠時段 (00:30-08:30): 輸入 1元/百萬tokens, 輸出 4元/百萬tokens (5折)'
            }
        </div>
    </div>

    <!-- Sentence Analysis -->
    <h2 style="margin: 30px 0 20px 0; color: #374151; font-size: 20px;">句型結構樹狀視覺化</h2>
`;

      // Add sentence analysis sections with collapsible functionality
      passageStructures.forEach((structure, index) => {
        const sentence = structure.sentence;
        const analysis = structure.analysis;
        
        html += `
    <div class="sentence-section">
        <div class="sentence-header" onclick="toggleSentence(${index})">
            <div class="sentence-text">${sentence}</div>
            <div class="chevron" id="chevron-${index}">></div>
        </div>
        <div class="sentence-content" id="content-${index}">
            <div class="analysis-container">
                <div class="analysis-section">
                    <div class="section-label">主句句型:</div>
                    <div class="section-content">
                        <span class="pattern-highlight">${analysis.pattern || analysis.mainType || 'S+V+O+A'}</span>
                        ${analysis.legendStructure ? `
                        <div class="legend-structure">
                            <div class="legend-title">Legend結構:</div>
                            <div class="legend-content">
                                ${analysis.legendStructure.split('+').map(part => `<span class="legend-item">${part.trim()}</span>`).join('')}
                            </div>
                        </div>
                        ` : ''}
                    </div>
                </div>
                
                <div class="analysis-section">
                    <div class="section-label">細分:</div>
                    <div class="section-content">${analysis.detailedAnalysis || analysis.details || 'subject, verb, directObject, indirectObject, complement, adverbial'}</div>
                </div>
                
                <div class="analysis-section">
                    <div class="section-label">細細分:</div>
                    <div class="section-content">${analysis.clauseAnalysis || (analysis.subclauses && Array.isArray(analysis.subclauses) ? analysis.subclauses.join('、') : 'modal verb (would)、prepositional phrase (to our customers)')}</div>
                </div>
            </div>
            
            ${structure.conditionClause ? `
            <div class="analysis-container" style="margin-top: 15px;">
                <div class="analysis-section">
                    <div class="section-label">條件從句:</div>
                    <div class="section-content">
                        <div style="margin-bottom: 10px;">
                            <strong>Subordinate Clause</strong>
                        </div>
                        <div style="margin-bottom: 10px;">
                            <strong>從句</strong>
                        </div>
                        <div class="subordinate-json">
                            "clause":"${structure.conditionClause.content}"<br>
                            "pattern":"<span class="pattern-highlight">${structure.conditionClause.pattern || 'S+V+C'}</span>"<br>
                            "basicStructure":"${structure.conditionClause.basicStructure || 'Subject + Verb + Complement'}"<br>
                            "legendStructure":"${structure.conditionClause.legendStructure || 'our display books(S) + are(V) + in poor condition(C)'}"
                        </div>
                    </div>
                </div>
            </div>
            ` : ''}
        </div>
    </div>
`;
      });

      html += `
    <div class="footer">
        <div class="timestamp">報告生成時間: ${timestamp}</div>
        <div style="margin-top: 10px;">由 EnglishDaily 英文學習平台生成</div>
        <div style="margin-top: 5px;">成本計算基於 DeepSeek API 官方定價</div>
    </div>

    <script>
        function toggleSentence(index) {
            const content = document.getElementById('content-' + index);
            const chevron = document.getElementById('chevron-' + index);
            
            if (content.classList.contains('expanded')) {
                content.classList.remove('expanded');
                chevron.classList.remove('expanded');
            } else {
                content.classList.add('expanded');
                chevron.classList.add('expanded');
            }
        }
        

    </script>
</body>
</html>`;

      return html;
    };

    const htmlContent = generateHTML();
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `EnglishDaily_句型分析報告_${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Show loading state while data is being fetched
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner isLoading={true} progress={0} currentStep="Loading ESS data..." />
          <p className="mt-4 text-gray-600">Loading ESS Toolbox data from database...</p>
        </div>
      </div>
    );
  }

  // Show error state if data loading failed
  if (dataError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️ Error Loading Data</div>
          <p className="text-gray-600 mb-4">{dataError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading Spinner */}
      <LoadingSpinner 
        isLoading={isLoading} 
        progress={progress} 
        currentStep={currentStep} 
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">ESS Toolbox</h1>
              <p className="text-gray-600 text-sm mt-1">English Sentence Structure 教學工具箱</p>
            </div>
            <div className="flex space-x-3 items-center">
              {/* DeepSeek Stories Dropdown */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Story:</span>
                <select
                  value={selectedStoryId}
                  onChange={(e) => handleSelectStory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white min-w-[260px]"
                >
                  <option value="">-- Select DeepSeek Story --</option>
                  {dsStories.map(story => (
                    <option key={story.id} value={story.id}>
                      {story.title || story.category || 'Story'}
                    </option>
                  ))}
                </select>
              </div>
            {/* Category */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Category:</span>
              <select
                value={dsCategory}
                onChange={(e) => setDsCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option>Action</option>
                <option>Characters</option>
                <option>Criminal Acts</option>
                <option>Family</option>
                <option>ESS</option>
              </select>
            </div>
            {/* Difficulty */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Difficulty:</span>
              <select
                value={dsDifficulty}
                onChange={(e) => setDsDifficulty(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">--</option>
                <option>Easy</option>
                <option>Medium</option>
                <option>Hard</option>
              </select>
            </div>
              <button
                onClick={downloadJSON}
                disabled={!deepseekApiResponse}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  deepseekApiResponse ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={deepseekApiResponse ? 'Download analysis JSON' : 'No analysis data available'}
              >
                JSON
              </button>
              <button
                onClick={handleSaveToSupabase}
                disabled={saving || (!importedData && !deepseekApiResponse && !inputText)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  saving ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
                title="Save current JSON to Supabase"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer">
                Import
                <input type="file" accept=".json" onChange={importJSON} className="hidden" />
              </label>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              { id: 'essToolbox', label: 'ESS Toolbox', icon: Layers },
              { id: 'analyze', label: '句型分析', icon: Target },
              { id: 'visualization', label: 'Passage 句型結構樹狀視覺化', icon: TreePine },
              { id: 'quiz', label: '互動練習測驗', icon: BarChart3 },
              { id: 'rewrite', label: '句型改寫練習', icon: Edit3 }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={16} className="mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {activeTab === 'essToolbox' && <ESSToolboxContent />}
        {activeTab === 'analyze' && <AnalysisTab 
          inputText={inputText}
          setInputText={setInputText}
          analyzedSentences={analyzedSentences}
          isLoading={isLoading}
          analyzePassage={analyzePassage}
        />}
        {activeTab === 'visualization' && <VisualizationTab 
          passageStructures={passageStructures}
          expandedItems={expandedItems}
          toggleExpand={toggleExpand}
          downloadHTML={downloadHTML}
          analysisStats={analysisStats}
          inputText={inputText}
        />}
        {activeTab === 'quiz' && <QuizTab 
          quizQuestions={quizQuestions}
          currentQuizIndex={currentQuizIndex}
          quizAnswers={quizAnswers}
          handleQuizAnswer={handleQuizAnswer}
          nextQuizQuestion={nextQuizQuestion}
          prevQuizQuestion={prevQuizQuestion}
        />}
        {activeTab === 'rewrite' && <RewriteTab 
          rewriteExercises={rewriteExercises}
          currentRewriteIndex={currentRewriteIndex}
          rewriteAnswers={rewriteAnswers}
          selectedRewriteType={selectedRewriteType}
          showExample={showExample}
          checkResult={checkResult}
          rewriteOptions={rewriteOptions}
          handleRewriteAnswer={handleRewriteAnswer}
          handleRewriteTypeChange={handleRewriteTypeChange}
          checkRewriteAnswer={checkRewriteAnswer}
          showRewriteExample={showRewriteExample}
          clearRewriteAnswer={clearRewriteAnswer}
          nextRewriteExercise={nextRewriteExercise}
          prevRewriteExercise={prevRewriteExercise}
        />}
      </main>
    </div>
  );
};

export default ESSToolboxEnhanced; 